import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../customization-page.css';
import { StoreState } from '../../../store/store';
import { CustomizationObjectType, Modality, ModalityDirection, ModelVisibility } from '../../../store/global-types/customization-types';
import { doseSelectors, metadataItemRemoved, metadataItemUpdated, modelCustomizationDescriptionUpdated, modelVisibilityUpdated, scrollToViewFromModelCustomizationMetadataRemoved, activeTagFilterToggled, activeModalityFilterToggled, activeVersionTagFilterToggled as doseActiveVersionTagFilter } from '../../../store/dose/doseSlice';
import { RTSTRUCT_METADATA_ATTRIBUTE_PRESETS } from '../../../store/contouring/contouring-types';
import DoseRoiTable from './DoseRoiTable';
import DoseSelectionRules from '../../selection/dose/DoseSelectionRules';
import DoseOutputForm from './DoseOutputForm';
import CustomizationSection from '../CustomizationSection';
import CustomizationForm from '../CustomizationForm';
import { makeSelectDoseRoisForOutput, makeSelectFormValidationErrorsForOptimization, makeSelectIsAnyMetadataForOutputModified, makeSelectIsAnyRoiForOutputModified, makeSelectIsAnyTargetForOutputModified, makeSelectIsAnyTriggerForBaseModified } from '../../../store/dose/doseSelectors';
import DoseTargetSection from './DoseTargetSection';
import DoseOptimizationForm from './DoseOptimizationForm';
import { collectDicomAttributeIds, computeValidationStatus, filterTriggerFormErrors } from '../../../store/global-types/store-errors';

interface DoseCustomizationFormProps {
    outputId: string,
}

const DoseCustomizationForm = (props: DoseCustomizationFormProps) => {
    const { outputId } = props;
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const customizationOutput = useSelector((state: StoreState) => doseSelectors.selectOutputById(state, outputId));
    const customizationBase = useSelector((state: StoreState) => customizationOutput ? doseSelectors.selectCustomizationBaseById(state, customizationOutput.modelCustomizationBaseId) : undefined);
    const model = useSelector((state: StoreState) => customizationBase ? doseSelectors.selectModelById(state, customizationBase.modelId) : undefined);
    const validationEntities = useSelector(doseSelectors.selectCustomizationValidationErrorEntities);
    const formValidationErrors = useSelector(doseSelectors.selectFormValidationErrors);
    const dicomRuleEntities = useSelector(doseSelectors.selectDicomRuleEntities);
    const outputMetadata = useSelector(doseSelectors.selectOutputMetadataEntities);
    const originalMetadata = useSelector(doseSelectors.selectOriginalOutputMetadataEntities);
    
    // Get active filters for visual feedback
    const activeTags = useSelector(doseSelectors.selectActiveTags);
    const activeVersionTags = useSelector(doseSelectors.selectActiveVersionTags);
    const activeInputModalities = useSelector(doseSelectors.selectActiveInputModalities);
    const activeOutputModalities = useSelector(doseSelectors.selectActiveOutputModalities);
    const [isOutputSectionExpanded, setOutputSectionExpanded] = useState(true);
    const [isRoisSectionExpanded, setRoisSectionExpanded] = useState(true);
    const [isTargetsSectionExpanded, setTargetsSectionExpanded] = useState(true);
    const [isOptimizationSectionExpanded, setOptimizationSectionExpanded] = useState(true);

    const selectDoseRoisForOutput = useMemo(makeSelectDoseRoisForOutput, []);
    const doseRois = useSelector((state: StoreState) => selectDoseRoisForOutput(state, outputId));

    const selectIsAnyRoiForOutputModified = useMemo(makeSelectIsAnyRoiForOutputModified, []);
    const isAnyRoiModified = useSelector((state: StoreState) => selectIsAnyRoiForOutputModified(state, outputId));

    const selectIsAnyTargetForOutputModified = useMemo(makeSelectIsAnyTargetForOutputModified, []);
    const isAnyTargetModified = useSelector((state: StoreState) => selectIsAnyTargetForOutputModified(state, outputId));

    const selectIsAnyMetadataForOutputModified = useMemo(makeSelectIsAnyMetadataForOutputModified, []);
    const isAnyMetadataModified = useSelector((state: StoreState) => selectIsAnyMetadataForOutputModified(state, outputId));

    const selectIsAnyTriggerRuleForBaseModified = useMemo(makeSelectIsAnyTriggerForBaseModified, []);
    const isAnyTriggerRuleModified = useSelector((state: StoreState) => customizationOutput ? selectIsAnyTriggerRuleForBaseModified(state, customizationOutput.modelCustomizationBaseId) : false);

    const selectOptimizationErrors = useMemo(makeSelectFormValidationErrorsForOptimization, []);
    const optimizationErrors = useSelector((state: StoreState) => selectOptimizationErrors(state, outputId));


    const handleToggleOutputSectionExpanded = useCallback(() => {
        setOutputSectionExpanded(!isOutputSectionExpanded);
    }, [isOutputSectionExpanded]);

    const handleToggleRoisSectionExpanded = useCallback(() => {
        setRoisSectionExpanded(!isRoisSectionExpanded);
    }, [isRoisSectionExpanded]);

    const handleToggleTargetsSectionExpanded = useCallback(() => {
        setTargetsSectionExpanded(!isTargetsSectionExpanded);
    }, [isTargetsSectionExpanded]);

    const handleToggleOptimizationSectionExpanded = useCallback(() => {
        setOptimizationSectionExpanded(!isOptimizationSectionExpanded);
    }, [isOptimizationSectionExpanded]);

    const onSetModelVisibility = useCallback((modelId: string, visibility: ModelVisibility) => {
        dispatch(modelVisibilityUpdated({ modelId, visibility }));
    }, [dispatch]);

    const onDescriptionChanged = useCallback((text: string) => {
        if (customizationBase) {
            dispatch(modelCustomizationDescriptionUpdated({ customizationBaseId: customizationBase.id, description: text }));
        }
    }, [dispatch, customizationBase]);

    const onMetadataItemUpdated = useCallback((metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean) => {
        dispatch(metadataItemUpdated({ metadataId, attribute, value, isUndoOperation }));
    }, [dispatch]);

    const onMetadaItemRemoved = useCallback((metadataId: string) => {
        dispatch(metadataItemRemoved(metadataId));
    }, [dispatch]);

    const removeScrollToViewFromMetadataItem = useCallback((metadataId: string) => {
        dispatch(scrollToViewFromModelCustomizationMetadataRemoved(metadataId));
    }, [dispatch]);

    const handleToggleModelTag = useCallback((tag: string) => {
        dispatch(activeTagFilterToggled(tag));
    }, [dispatch]);

    const handleToggleModelModality = useCallback((modality: Modality, direction: ModalityDirection) => {
        dispatch(activeModalityFilterToggled({ modality, direction }));
    }, [dispatch]);

    const handleToggleModelVersionTag = useCallback((tag: string) => {
        dispatch(doseActiveVersionTagFilter(tag));
    }, [dispatch]);

    // sanity check that we have retrieved valid objects from redux store
    // (this check has to be done after all hooks calls)
    if (customizationOutput === undefined || customizationBase === undefined || model === undefined) {
        return null;
    }

    const outputHasBackendErrors = validationEntities[outputId]?.type === CustomizationObjectType.CustomizationOutput;
    const roisHaveBackendErrors = customizationOutput.rois.some(rId => validationEntities[rId]?.type === CustomizationObjectType.Roi);
    const targetsHaveBackendErrors = customizationOutput.targets.some(tId => validationEntities[tId]?.type === CustomizationObjectType.Target);

    const roiFormErrors = formValidationErrors.filter(e => customizationOutput.rois.includes(e.itemId) && e.itemType === CustomizationObjectType.Roi);
    const roisValidationStatus = computeValidationStatus(roisHaveBackendErrors, roiFormErrors);
    const outputValidationStatus = computeValidationStatus(outputHasBackendErrors);
    const targetsValidationStatus = computeValidationStatus(targetsHaveBackendErrors);
    const optimizationValidationStatus = computeValidationStatus(false, optimizationErrors);

    const dicomAttributeIds = collectDicomAttributeIds(customizationBase.dicomRules, dicomRuleEntities);
    const triggerFormErrors = filterTriggerFormErrors(formValidationErrors, customizationBase.aeTitleRules, customizationBase.dicomRules, dicomAttributeIds);

    return (
        <CustomizationForm
            model={model}
            customizationBase={customizationBase}
            customizationOutput={customizationOutput}
            validationEntities={validationEntities}
            triggerFormValidationErrors={triggerFormErrors}
            outputMetadata={outputMetadata}
            originalMetadata={originalMetadata}
            metadataPresets={RTSTRUCT_METADATA_ATTRIBUTE_PRESETS}
            isAnyMetadataModified={isAnyMetadataModified}
            isAnyTriggerRuleModified={isAnyTriggerRuleModified}
            onModelTagToggled={handleToggleModelTag}
            onModelVersionTagToggled={handleToggleModelVersionTag}
            onModelModalityToggled={handleToggleModelModality}
            onSetModelVisibility={onSetModelVisibility}
            onDescriptionChanged={onDescriptionChanged}
            onMetadataItemUpdated={onMetadataItemUpdated}
            onMetadaItemRemoved={onMetadaItemRemoved}
            removeScrollToViewFromMetadataItem={removeScrollToViewFromMetadataItem}
            isMetadataSectionDisabled={true}
            isDicomSelectionDisabled={true}
            hideTargetFile={true}
            activeTags={activeTags}
            activeVersionTags={activeVersionTags}
            activeInputModalities={activeInputModalities}
            activeOutputModalities={activeOutputModalities}
            selectionRules={(
                <DoseSelectionRules
                    customizationBaseId={customizationBase.id}
                    isUndoDisabled={true}
                    setUndoState={() => { }}
                    heading={`model-selection-for-${customizationBase.id}`}
                    hideCustomizationName
                    showMultipleOutputsWarning={customizationBase.outputs.length > 1}
                />
            )}
        >

            <CustomizationSection
                label={t('customizationPage.dose.outputSection.title')}
                isModified={customizationOutput.isModified}
                validationStatus={outputValidationStatus}
                collapsedMessage={t('customizationPage.dose.outputSection.message')}
                isExpanded={isOutputSectionExpanded}
                onClick={handleToggleOutputSectionExpanded}
            >
                <DoseOutputForm outputId={outputId} />
            </CustomizationSection>

            <CustomizationSection
                label={t('customizationPage.dose.targetsSection.title')}
                isModified={isAnyTargetModified}
                validationStatus={targetsValidationStatus}
                itemCount={customizationOutput.targets.length}
                collapsedMessage={t('customizationPage.dose.targetsSection.message')}
                isExpanded={isTargetsSectionExpanded}
                onClick={handleToggleTargetsSectionExpanded}
            >
                <DoseTargetSection
                    outputId={outputId}
                />
            </CustomizationSection>

            <CustomizationSection
                label={t('customizationPage.dose.roisSection.title')}
                isModified={isAnyRoiModified}
                validationStatus={roisValidationStatus}
                itemCount={customizationOutput.rois.length}
                collapsedMessage={t('customizationPage.dose.roisSection.message')}
                isExpanded={isRoisSectionExpanded}
                onClick={handleToggleRoisSectionExpanded}
            >
                <DoseRoiTable
                    outputId={outputId}
                    roiIds={customizationOutput.rois}
                />
            </CustomizationSection>

            <CustomizationSection
                label={t('customizationPage.dose.optimizationSection.title')}
                isModified={customizationOutput.isModified}
                validationStatus={optimizationValidationStatus}
                collapsedMessage={t('customizationPage.dose.optimizationSection.message')}
                isExpanded={isOptimizationSectionExpanded}
                onClick={handleToggleOptimizationSectionExpanded}
                testId="optimization-section"
            >
                <DoseOptimizationForm outputId={outputId} />
            </CustomizationSection>

        </CustomizationForm >
    );
}

export default DoseCustomizationForm;
